<template>
  <div class="absolute inset-y-0 left-0 flex items-center pl-1">
    <div ref="containerRef" class="color-picker-container">
      <div class="color-input" :style="{ background: resultText }" @click="togglePicker"></div>
      <div v-if="isPickerOpen" ref="dropdownRef" class="picker-dropdown" :style="dropdownStyle">
        <div ref="panelRef" class="color-panel" :style="{ background: panelBackground }" @mousedown="startPanelDrag" @click.stop="panelClick">
          <div ref="colorThumbRef" class="color-thumb"></div>
        </div>
        <div class="hue-container">
          <div ref="hueSliderRef" class="hue-slider" @mousedown="startHueDrag" @click.stop="hueClick">
            <div ref="hueThumbRef" class="hue-thumb"></div>
          </div>
          <button class="eyedropper-btn" title="Pick color from page" @click="useEyedropper">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m2 22 1-1h3l9-9"></path>
              <path d="M3 21v-3l9-9"></path>
              <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"></path>
            </svg>
          </button>
        </div>
        <div class="alpha-slider">
          <label for="alpha-range">Alpha: </label>
          <input id="alpha-range" v-model.number="alphaValue" type="range" min="0" max="100" @input="onAlphaInput" />
          <span>{{ alphaValue }}%</span>
        </div>
        <div v-if="showResult" class="result-display">
          <div id="result-square" class="result-square" :style="{ background: currentColor }"></div>
          <div class="result-text-container">
            <span id="result-text">{{ resultText }}</span>
            <button id="format-cycle-btn" title="Cycle Format" @click="cycleFormat">↻</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <input
    :id="propDef?.name"
    :value="modelValue"
    type="text"
    pattern="^(?:#(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})|rgba\(\s*(?:25[0-5]|2[0-4]\d|1?\d{1,2})\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d{1,2})\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d{1,2})\s*,\s*(?:1(?:\.\d+)?|0(?:\.\d+)?)\s*\)|hsla\(\s*(?:360|3[0-5]\d|[12]\d{1,2}|[1-9]?\d)\s*,\s*(?:100|[0-9]?\d)%\s*,\s*(?:100|[0-9]?\d)%\s*,\s*(?:1(?:\.\d+)?|0(?:\.\d+)?)\s*\))$"
    class="block w-full rounded-md border-0 px-2 py-1.5 pl-8 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 invalid:border-red-500 invalid:ring-red-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
    @input="emit('update:modelValue', $event.target.value)"
    @focus="onInputFocus"
    @blur="onInputBlur($event)"
  />
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

  // Define component props and emits
  const props = defineProps({
    modelValue: {
      type: String,
      default: 'rgba(255, 0, 0, 1)',
    },
    propDef: {
      type: Object,
    },
    showResult: {
      type: Boolean,
      default: true,
    },
  });

  const emit = defineEmits(['update:modelValue']);

  const isTyping = ref(false);
  function onInputFocus() {
    isTyping.value = true;
  }
  function onInputBlur(e) {
    isTyping.value = false;
    if (e.target.validity.valid) {
      initValue();
    }
  }

  // Refs for DOM elements and state
  const isPickerOpen = ref(false);
  const containerRef = ref(null);
  const panelRef = ref(null);
  const colorThumbRef = ref(null);
  const hueSliderRef = ref(null);
  const hueThumbRef = ref(null);

  const currentHue = ref(0);
  const currentSat = ref(100);
  const currentVal = ref(100);
  const alphaValue = ref(100);

  const resultText = ref('');
  const resultFormats = ['hex6', 'hex8', 'rgba', 'hsla'];
  const isHex6Input = ref(false);
  const currentFormatIndex = ref(2);

  const dropdownRef = ref(null);
  const dropdownDirection = ref('down');
  const dropdownStyle = computed(() => {
    return dropdownDirection.value === 'up' ? { bottom: '25px', top: 'auto' } : { top: '25px', bottom: 'auto' };
  });

  // Internal non-reactive variables
  let isDraggingHue = false;
  let isDraggingPanel = false;
  let hueSliderRect = null;
  let panelRect = null;

  // [sv2] Watch value changes to handle case when value changes after initial load
  // this could be page refresh when on /embed url
  watch(
    () => props.modelValue,
    async () => {
      if (!isTyping.value) {
        initValue();
      }
    },
  );

  const panelBackground = computed(() => {
    return `hsl(${Math.round(currentHue.value)}, 100%, 50%)`;
  });

  const currentColor = computed(() => {
    const a = (alphaValue.value / 100).toFixed(2);
    const l = Math.round((currentVal.value / 100) * 50) + '%';
    const s = currentSat.value + '%';
    return `hsla(${Math.round(currentHue.value)}, ${s}, ${l}, ${a})`;
  });

  // Utility functions
  function hsvToRgb(h, s, v) {
    let c = v * s;
    let x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    let m = v - c;
    let r1, g1, b1;
    if (h < 60) {
      r1 = c;
      g1 = x;
      b1 = 0;
    } else if (h < 120) {
      r1 = x;
      g1 = c;
      b1 = 0;
    } else if (h < 180) {
      r1 = 0;
      g1 = c;
      b1 = x;
    } else if (h < 240) {
      r1 = 0;
      g1 = x;
      b1 = c;
    } else if (h < 300) {
      r1 = x;
      g1 = 0;
      b1 = c;
    } else {
      r1 = c;
      g1 = 0;
      b1 = x;
    }
    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255),
    };
  }

  function rgbToHex(r, g, b) {
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  }

  function updateResult() {
    if (isTyping.value) return;
    const rgb = hsvToRgb(currentHue.value, currentSat.value / 100, currentVal.value / 100);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    const aHex = Math.round((alphaValue.value / 100) * 255)
      .toString(16)
      .padStart(2, '0');
    const newColor = hex + aHex;

    const currentResult = {
      h: Math.round(currentHue.value),
      s: currentSat.value,
      v: currentVal.value,
      alpha: parseFloat(alphaValue.value.toFixed(2)),
      hex8: newColor,
      rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(alphaValue.value / 100).toFixed(2)})`,
    };

    let vNorm = currentVal.value / 100,
      sNorm = currentSat.value / 100;
    let l = vNorm * (1 - sNorm / 2);
    let sHSL = l === 0 || l === 1 ? 0 : (vNorm - l) / Math.min(l, 1 - l);
    currentResult.hsla = `hsla(${Math.round(currentHue.value)}, ${Math.round(sHSL * 100)}%, ${Math.round(l * 100)}%, ${(alphaValue.value / 100).toFixed(2)})`;

    // Determine display format (hex8, rgba, hsla)
    const format = resultFormats[currentFormatIndex.value];
    let displayText = '';
    if (format === 'hex6') {
      displayText = '#' + currentResult.hex8.slice(1, 7);
    } else if (format === 'hex8') {
      displayText = currentResult.hex8;
    } else if (format === 'rgba') {
      displayText = currentResult.rgba;
    } else if (format === 'hsla') {
      displayText = currentResult.hsla;
    }
    resultText.value = displayText;
    emit('update:modelValue', resultText.value);
  }

  // Hue drag handlers
  const startHueDrag = (e) => {
    isDraggingHue = true;
    hueSliderRect = hueSliderRef.value.getBoundingClientRect();
    handleHueDrag(e);
    window.addEventListener('pointermove', handleHueDrag);
    window.addEventListener('pointerup', stopHueDrag);
  };

  const handleHueDrag = (e) => {
    let x = e.clientX - hueSliderRect.left;
    const thumbW = hueThumbRef.value.offsetWidth;
    if (x < 0) x = 0;
    if (x > hueSliderRect.width - thumbW) x = hueSliderRect.width - thumbW;
    hueThumbRef.value.style.left = x + 'px';
    currentHue.value = 360 - (x / (hueSliderRect.width - thumbW)) * 360;
    updateResult();
  };

  const stopHueDrag = () => {
    isDraggingHue = false;
    window.removeEventListener('pointermove', handleHueDrag);
    window.removeEventListener('pointerup', stopHueDrag);
  };

  const hueClick = (e) => {
    hueSliderRect = hueSliderRef.value.getBoundingClientRect();
    let x = e.clientX - hueSliderRect.left;
    const thumbW = hueThumbRef.value.offsetWidth;
    if (x < 0) x = 0;
    if (x > hueSliderRect.width - thumbW) x = hueSliderRect.width - thumbW;
    hueThumbRef.value.style.left = x + 'px';
    currentHue.value = 360 - (x / (hueSliderRect.width - thumbW)) * 360;
    updateResult();
  };

  // Panel (saturation/value) drag handlers
  const startPanelDrag = (e) => {
    isDraggingPanel = true;
    panelRect = panelRef.value.getBoundingClientRect();
    handlePanelDrag(e);
    window.addEventListener('pointermove', handlePanelDrag);
    window.addEventListener('pointerup', stopPanelDrag);
  };

  function positionThumbs() {
    if (!hueSliderRef.value || !hueThumbRef.value || !panelRef.value || !colorThumbRef.value) return;
    hueSliderRect = hueSliderRef.value.getBoundingClientRect();
    const hueThumbWidth = hueThumbRef.value.offsetWidth;
    const xHue = ((360 - currentHue.value) / 360) * (hueSliderRect.width - hueThumbWidth);
    hueThumbRef.value.style.left = xHue + 'px';
    panelRect = panelRef.value.getBoundingClientRect();
    const colorThumbWidth = colorThumbRef.value.offsetWidth;
    const colorThumbHeight = colorThumbRef.value.offsetHeight;
    const xColor = (currentSat.value / 100) * (panelRect.width - colorThumbWidth);
    const yColor = (1 - currentVal.value / 100) * (panelRect.height - colorThumbHeight);
    colorThumbRef.value.style.left = xColor + 'px';
    colorThumbRef.value.style.top = yColor + 'px';
  }

  const handlePanelDrag = (e) => {
    let x = e.clientX - panelRect.left;
    let y = e.clientY - panelRect.top;
    const thumbW = colorThumbRef.value.offsetWidth;
    const thumbH = colorThumbRef.value.offsetHeight;
    if (x < 0) x = 0;
    if (x > panelRect.width - thumbW) x = panelRect.width - thumbW;
    if (y < 0) y = 0;
    if (y > panelRect.height - thumbH) y = panelRect.height - thumbH;
    colorThumbRef.value.style.left = x + 'px';
    colorThumbRef.value.style.top = y + 'px';
    currentSat.value = Math.round((x / (panelRect.width - thumbW)) * 100);
    currentVal.value = Math.round((1 - y / (panelRect.height - thumbH)) * 100);
    updateResult();
  };

  const stopPanelDrag = () => {
    isDraggingPanel = false;
    window.removeEventListener('pointermove', handlePanelDrag);
    window.removeEventListener('pointerup', stopPanelDrag);
  };

  const panelClick = (e) => {
    panelRect = panelRef.value.getBoundingClientRect();
    handlePanelDrag(e);
  };

  // Toggle picker dropdown
  const togglePicker = () => {
    isPickerOpen.value = !isPickerOpen.value;
    if (isPickerOpen.value) {
      nextTick(() => {
        const containerRect = containerRef.value.getBoundingClientRect();
        const dropdownHeight = dropdownRef.value.offsetHeight;
        if (window.innerHeight - containerRect.bottom < dropdownHeight) {
          dropdownDirection.value = 'up';
        } else {
          dropdownDirection.value = 'down';
        }
        // after DOM update for dropdownDirection, nest another nextTick for layout
        positionThumbs();
      });
    }
  };

  // Close the picker if clicking outside
  const handleClickOutside = (e) => {
    if (containerRef.value && !containerRef.value.contains(e.target)) {
      isPickerOpen.value = false;
    }
  };

  function processHexInit(hex) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const { h, s, v } = rgbToHsv(r, g, b);
    currentHue.value = h;
    currentSat.value = Math.round(s);
    currentVal.value = Math.round(v);
    const hasAlpha = hex.length === 8;
    const alphaHex = hasAlpha ? hex.slice(6, 8) : 'FF';
    alphaValue.value = Math.round((parseInt(alphaHex, 16) / 255) * 100);
    isHex6Input.value = !hasAlpha;
    currentFormatIndex.value = resultFormats.indexOf(hasAlpha ? 'hex8' : 'hex6');
  }

  function initValue() {
    const init = props.modelValue;
    if (init && init.startsWith('rgba')) {
      const match = init.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*(\d*\.?\d+)\)/);
      if (match) {
        const [, r, g, b, a] = match.map(Number);
        const hsv = rgbToHsv(r, g, b);
        currentHue.value = hsv.h;
        currentSat.value = Math.round(hsv.s);
        currentVal.value = Math.round(hsv.v);
        alphaValue.value = Math.round((a ?? 1) * 100);
        nextTick(() => {
          positionThumbs();
          updateResult();
        });
      }
    } else if (init && init.startsWith('#')) {
      const hex = init.replace(/^#/, '');
      if (hex.length === 6 || hex.length === 8) {
        processHexInit(hex);
      }
      nextTick(() => {
        positionThumbs();
        updateResult();
      });
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    initValue();
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  function parseRgba(rgbaString) {
    const regex = /rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]*\.?[0-9]+)\s*\)/i;
    const match = rgbaString.match(regex);
    if (match) {
      return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10),
        a: parseFloat(match[4]),
      };
    }
    return { r: 0, g: 0, b: 0, a: 1 };
  }

  function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((x) => x + x)
        .join('');
    }
    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  function rgbToHsv(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    let max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h,
      s,
      v = max;
    let d = max - min;
    s = max === 0 ? 0 : d / max;
    if (max === min) {
      h = 0;
    } else {
      if (max === r) {
        h = (g - b) / d + (g < b ? 6 : 0);
      } else if (max === g) {
        h = (b - r) / d + 2;
      } else {
        h = (r - g) / d + 4;
      }
      h *= 60;
    }
    return { h, s: s * 100, v: v * 100 };
  }

  // Cycle through color format options
  const cycleFormat = () => {
    currentFormatIndex.value = (currentFormatIndex.value + 1) % resultFormats.length;
    updateResult();
  };

  function onAlphaInput() {
    if (isHex6Input.value) {
      isHex6Input.value = false;
      currentFormatIndex.value = resultFormats.indexOf('hex8');
    }
    updateResult();
  }

  // Use the EyeDropper API if available
  const useEyedropper = async () => {
    if (window.EyeDropper) {
      try {
        const eyeDropper = new EyeDropper();
        const result = await eyeDropper.open();
        // Parse picked color and initialize state
        let hex = result.sRGBHex.replace(/^#/, '');
        if (hex.startsWith('rgba')) {
          const rgba = parseRgba(hex);
          hex = rgbToHex(rgba.r, rgba.g, rgba.b).replace('#', '');
        }
        processHexInit(hex);
        alphaValue.value = 100;
        await nextTick();
        positionThumbs();
        updateResult();
      } catch (e) {
        console.error(e);
      }
    }
  };
</script>

<style scoped>
  .color-picker-container {
    position: relative;
    font-family: sans-serif;
  }

  .color-input {
    width: 25px;
    height: 25px;
    border: 1px solid #ccc;
    cursor: pointer;
  }

  .picker-dropdown {
    position: absolute;
    top: 36px;
    left: 0;
    z-index: 100;
    background: #fff;
    border: 1px solid #ccc;
    padding: 8px;
  }

  .color-panel {
    position: relative;
    width: 100%;
    height: 200px;
    margin-bottom: 8px;
    background: red;
    cursor: pointer;
  }
  .color-panel::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0));
    z-index: 0;
  }
  .color-panel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000);
    z-index: 0;
  }
  .color-thumb {
    position: absolute;
    top: 0;
    left: 0;
    width: 12px;
    height: 12px;
    border: 2px solid #fff;
    border-radius: 50%;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    z-index: 2;
    cursor: pointer;
  }

  .hue-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .hue-slider {
    position: relative;
    flex: 1;
    height: 20px;
    background: linear-gradient(to right, red, magenta, blue, cyan, lime, yellow, red);
    cursor: pointer;
    border: 1px solid #ccc;
  }
  .hue-thumb {
    position: absolute;
    top: 0;
    left: 0;
    width: 12px;
    height: 20px;
    border: 2px solid #fff;
    z-index: 2;
    cursor: pointer;
    background: transparent;
  }

  .eyedropper-btn {
    border: 1px solid #ccc;
    background: #f9f9f9;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 24px;
  }
  .eyedropper-btn svg {
    width: 20px;
    height: 20px;
    stroke: #000;
  }

  .alpha-slider {
    text-align: center;
    margin: 8px 0;
    display: flex;
    width: 287px;
  }
  .alpha-slider input[type='range'] {
    width: 200px;
    margin: 3.3px;
  }

  .result-display {
    width: 300px;
    margin: 20px auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: sans-serif;
  }
  .result-square {
    width: 24px;
    height: 24px;
    border: 1px solid #ccc;
  }
  .result-text-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  #result-text {
    flex: 1;
  }
  #format-cycle-btn {
    padding: 2px 4px;
    font-size: 12px;
    cursor: pointer;
  }
</style>
